import { Card, Grid, Typography } from "@material-ui/core";
import { SelectOptions } from "common/assets/data/options";
import { ActivityType, InvestmentClass } from "common/backend/types/utils";
import { UserField, UserInputTypeKey } from "common/components/input/fields";
import { SubformProps } from "common/components/SubformProps";
import { UpdateUserManageable, UserManageable } from "common/store/user/types";
import React from "react";
import { useTranslation } from "react-i18next";

interface ActivityDetailsProps extends SubformProps<UserManageable | UpdateUserManageable> {
  indexkey: ActivityType;
}

interface SkillDetailsProps extends SubformProps<UserManageable | UpdateUserManageable> {
  indexkey: InvestmentClass;
}

export const ActivityDetails = (props: ActivityDetailsProps) => {
  // Hooks
  const { t } = useTranslation();

  // Options
  const disabled =
    "notProvidingExperience" in props.values &&
    props.values.notProvidingExperience;

  const title = () => {
    switch (props.indexkey) {
      case ActivityType.WP_SERVICE:
        return t("WP Services");
      default:
        return undefined;
    }
  };

  return (
    <Grid container direction="column" spacing={5}>
      <Grid item>
        <Typography variant="subtitle2" className="section-title">
          {title()}
        </Typography>

        <Card className="card--nested">
          <Grid container spacing={5} direction="column">
            <Grid item>
              <UserField
                id={"experience" as UserInputTypeKey}
                subkey="levelWithoutAdvice"
                disabled={disabled}
                {...props}
              />
            </Grid>

            <Grid item>
              <UserField
                id={"experience" as UserInputTypeKey}
                subkey="levelWithAdvice"
                disabled={disabled}
                {...props}
              />
            </Grid>

            <Grid item>
              <UserField
                id={"experience" as UserInputTypeKey}
                subkey="levelWithAssetManagement"
                disabled={disabled}
                {...props}
              />
            </Grid>
          </Grid>
        </Card>
      </Grid>

      <Grid item>
        <Typography variant="subtitle2" className="section-title">
          {t("Experience in terms of number of transactions and average amount")}
        </Typography>

        <Card className="card--nested">
          <Grid container spacing={5} direction="column">
            <Grid item>
              <UserField
                id={"experience" as UserInputTypeKey}
                subkey="transactionsPerYear"
                disabled={disabled}
                {...props}
              />
            </Grid>

            <Grid item>
              <UserField
                id={"experience" as UserInputTypeKey}
                subkey="volumePerYear"
                disabled={disabled}
                {...props}
              />
            </Grid>
          </Grid>
        </Card>
      </Grid>
    </Grid>
  );
};

export const SkillDetails = (props: SkillDetailsProps) => {
  // Options
  const disabled =
    "notProvidingExperience" in props.values &&
    props.values.notProvidingExperience;

  const option = SelectOptions().investmentClass.find((option: any) => option.value === props.indexkey);

  return (
    <React.Fragment>
      <Typography variant="subtitle2" className="section-title">
        {option ? option.label : ''}
      </Typography>

      <Card className="card--nested">
        <Grid container spacing={5} direction="column">
          <Grid item>
            <UserField
              id={"experience" as UserInputTypeKey}
              subkey="hasKnowledge"
              disabled={disabled}
              {...props}
            />
          </Grid>

          <Grid item>
            <UserField
              id={"experience" as UserInputTypeKey}
              subkey="level"
              disabled={disabled}
              {...props}
            />
          </Grid>
        </Grid>
      </Card>
    </React.Fragment>
  );
};

export const UserExperienceSubform = (props: SubformProps<UpdateUserManageable>) => {
  // Hooks
  const { t } = useTranslation();

  const skillTypes = Object.values(InvestmentClass);
  const activityTypes = Object.values(ActivityType);

  return (
    <Grid container direction="column" spacing={5}>
      <Grid item xs={12}>
        <Typography variant="body1" color="textSecondary">
          {t("Please indicate separately for each product group what knowledge and experience you have.")}
        </Typography>
      </Grid>

      {skillTypes.map((
        value: any, index: number
      ) =>
        <Grid item key={index}>
          <SkillDetails indexkey={value} {...props} />
        </Grid>)}

      {activityTypes.map((
        value: any, index: number
      ) =>
        <Grid item key={index}>
          <ActivityDetails indexkey={value} {...props} />
        </Grid>)}

      <Grid item className="checkbox-center-container" xs={12}>
        <UserField id={"notProvidingExperience" as UserInputTypeKey} {...props} />
      </Grid>
    </Grid>
  );
}
